/**
 * use-transfer-upload — page-lokale Upload-Engine für Transfers (Sprint T2).
 *
 * BEWUSST kein globaler Provider (Spec-Architekturentscheidung): Transfers
 * sind ein „Ich verschicke jetzt was"-Flow. Der Verlustfall bei Navigation
 * wird vom zweistufigen Verlassen-Schutz der TransferPage gedeckt
 * (beforeunload + In-App-Nav-Guard); beim Unmount werden laufende XHRs
 * abgebrochen, der Draft bleibt (24h-Cleanup-Worker räumt ihn).
 *
 * Das XHR-Muster ist dem UploadProvider NACHGEBAUT (nicht reused — der ist
 * asset-gekoppelt): xhr.upload.onprogress, Abort-Disambiguation (onabort =
 * User → kein Retry; onerror status 0 = Netz → Retry), interruptible Backoff.
 *
 * Retry-Achsen GETRENNT (patterns/api-streams.md):
 *  - transiente Fehler (Netz/502/503/504): max. 3 Versuche gesamt
 *    (Parität UploadProvider MAX_UPLOAD_ATTEMPTS), Backoff 3s·n.
 *  - 429-SPLIT (Codex-PR-R1): Retry-After ≤ 30 s = Slot-Semaphore (Gate MAX 2)
 *    → kurzer Backoff (clamp 1–30 s), Cap 5, Status „wartet auf Slot".
 *    Retry-After > 30 s = User-Rate-Limit (60 Uploads/15 min, rate-limit.ts;
 *    ≥ TRANSFER_MAX_FILES damit ein voller Ordner-Batch durchläuft)
 *    → ECHTES Wartefenster (bis 16 min, eigener Zähler Cap 3) — sonst failen
 *    über-Limit-Dateien eines Batches deterministisch am Slot-Cap.
 *  - 409 (Transfer nicht mehr draft — Revoke in anderem Tab / Worker-Race):
 *    TERMINAL, kein Retry — Queue stoppt, nur noch „Neuer Transfer".
 *  - 413/400/…: sofortiger Fehler, manueller Retry-Button bleibt.
 * Manueller Retry resettet alle Zähler.
 *
 * Concurrency-Limit 2 == Server-Gate (MAX_CONCURRENT_TRANSFER_UPLOADS) →
 * eigene Uploads kollidieren nicht mit sich selbst; 429 entsteht nur durch
 * andere Sender.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import pLimit from 'p-limit';
import { api, ApiError } from '../lib/api';
import { isFileReadable, FILE_CHANGED_MESSAGE } from '../lib/file-readable';
import {
  TRANSFER_MAX_FILE_BYTES,
  TRANSFER_MAX_FILES,
  TRANSFER_MAX_TOTAL_BYTES,
  type TransferExpiryDays,
} from '../../shared/transfer-constants';

// Re-Export für Bestands-Importe (TransferPage-Test) — SSOT liegt in lib/file-readable.
export { FILE_CHANGED_MESSAGE };

const CONCURRENCY = 2;
const TRANSIENT_MAX_ATTEMPTS = 3;
const RATE_MAX_ATTEMPTS = 5;
const TRANSIENT_RETRY_BASE_MS = 3000;
const DEFAULT_RETRY_AFTER_MS = 15_000; // Server RETRY_AFTER_SEC = 15
const RETRY_AFTER_MIN_MS = 1000;
const RETRY_AFTER_MAX_MS = 30_000;
// 429-Split-Schwelle (api-streams.md): Retry-After über 30 s kommt vom
// user-keyed Rate-Limit (Fenster bis 900 s), nicht vom Slot-Gate.
const RATE_LIMIT_THRESHOLD_MS = 30_000;
const RATE_LIMIT_MAX_WAIT_MS = 16 * 60_000; // > 15-min-Fenster
const RATE_LIMIT_MAX_WAITS = 3;
const RETRYABLE_TRANSIENT_STATUS = new Set([0, 502, 503, 504]);

export const FLOW_ERROR_MESSAGE = 'Transfer wurde bereits abgeschlossen oder widerrufen.';

export const FOLDER_EMPTY_REASON = 'Ordner ist leer';
export const FOLDER_UNREADABLE_REASON = 'Ordner konnte nicht gelesen werden';
/** Defensive Traversal-Obergrenze — weit über TRANSFER_MAX_FILES (20; addFiles
 * cappt real), schützt nur vor pathologischen Riesen-Bäumen beim Rekursieren. */
const DIR_TRAVERSAL_CAP = 200;
export const FOLDER_CAP_REASON = `Ordner enthält zu viele Dateien — nach ${DIR_TRAVERSAL_CAP} abgebrochen`;

/** readEntries liefert pro Aufruf nur einen BATCH (Chrome: max 100) und MUSS
 * bis zum leeren Batch wiederholt werden — sonst fehlen Dateien ab #101. */
function readAllEntries(reader: FileSystemDirectoryReader): Promise<FileSystemEntry[]> {
  return new Promise((resolve, reject) => {
    const all: FileSystemEntry[] = [];
    const step = (): void =>
      reader.readEntries((batch) => {
        if (batch.length === 0) return resolve(all);
        all.push(...batch);
        step();
      }, reject);
    step();
  });
}

function entryFile(entry: FileSystemFileEntry): Promise<File> {
  return new Promise((resolve, reject) => entry.file(resolve, reject));
}

/** Rekursives Einsammeln: Dateien aus Unterordnern bekommen ihren relativen
 * Pfad als Namens-Prefix („Artwork/RZ_x.ai") — Sender-Queue zeigt ihn 1:1,
 * server-seitig macht sanitizeDisplayFilename daraus „Artwork_RZ_x.ai".
 * Der ROOT-Ordnername wird bewusst NICHT vorangestellt (Projektname steckt
 * im Transfer-Kontext; 255-Zeichen-Budget nicht mit Langnamen verbrennen).
 * Dotfiles (.DS_Store & Co) werden übersprungen. */
async function collectDir(
  dir: FileSystemDirectoryEntry,
  prefix: string,
  out: File[],
  budget: { left: number; truncated: boolean },
): Promise<void> {
  const entries = await readAllEntries(dir.createReader());
  for (const entry of entries) {
    if (budget.left <= 0) {
      budget.truncated = true;
      return;
    }
    if (entry.name.startsWith('.')) continue;
    if (entry.isFile) {
      const raw = await entryFile(entry as FileSystemFileEntry);
      out.push(
        prefix
          ? new File([raw], `${prefix}${raw.name}`, { type: raw.type, lastModified: raw.lastModified })
          : raw,
      );
      budget.left -= 1;
    } else if (entry.isDirectory) {
      await collectDir(entry as FileSystemDirectoryEntry, `${prefix}${entry.name}/`, out, budget);
    }
  }
}

/**
 * Drop-Auflösung mit Ordner-Support (SwissTransfer/WeTransfer-Gewohnheit):
 * gedroppte Ordner werden rekursiv traversiert und ihre Dateien einzeln in
 * die Queue gegeben. Hintergrund: ein Ordner-Drop liefert sonst ein
 * unlesbares Pseudo-File (size = APFS-Verzeichnisgröße, z.B. 224 B) — so
 * entstanden die kryptischen „Netzwerkfehler"-Uploads vom 2026-07-15.
 *
 * Der synchrone Teil (Entry-/File-Capture) MUSS im Drop-Event laufen —
 * DataTransfer-Items sind nach dem Event neutered; das async-Traversal
 * danach ist safe (Entries bleiben gültig). Browser ohne webkitGetAsEntry
 * → Fallback auf dt.files (die Upload-Lesbarkeits-Probe bleibt Backstop).
 * Leere/unlesbare/überlange Ordner landen als Rejection im Hinweis-Banner.
 */
export async function collectDroppedFiles(dt: DataTransfer): Promise<{
  files: Array<{ file: File; folder: string | null }>;
  preRejected: TransferRejection[];
}> {
  const items = Array.from(dt.items ?? []);
  const canInspect =
    items.length > 0 && typeof items[0].webkitGetAsEntry === 'function';
  if (!canInspect) {
    return { files: Array.from(dt.files).map((file) => ({ file, folder: null })), preRejected: [] };
  }

  // SYNC-Capture vor dem ersten await (siehe Doku oben).
  const files: Array<{ file: File; folder: string | null }> = [];
  const dirs: FileSystemDirectoryEntry[] = [];
  for (const item of items) {
    if (item.kind !== 'file') continue;
    const entry = item.webkitGetAsEntry();
    if (entry?.isDirectory) {
      dirs.push(entry as FileSystemDirectoryEntry);
    } else {
      const file = item.getAsFile();
      if (file) files.push({ file, folder: null });
    }
  }

  const preRejected: TransferRejection[] = [];
  const budget = { left: DIR_TRAVERSAL_CAP, truncated: false };
  for (const dir of dirs) {
    // Pro Ordner lokal sammeln → Dateien tragen den Ordnernamen als Gruppen-
    // Tag (die Queue-UI zeigt den Ordner als EINE Zeile, WeTransfer-Parität).
    const dirFiles: File[] = [];
    try {
      await collectDir(dir, '', dirFiles, budget);
    } catch {
      preRejected.push({ name: dir.name, reason: FOLDER_UNREADABLE_REASON });
      continue;
    }
    files.push(...dirFiles.map((file) => ({ file, folder: dir.name })));
    if (budget.truncated) {
      preRejected.push({ name: dir.name, reason: FOLDER_CAP_REASON });
      break; // weitere Ordner hätten kein Budget mehr
    }
    if (dirFiles.length === 0) {
      preRejected.push({ name: dir.name, reason: FOLDER_EMPTY_REASON });
    }
  }
  return { files, preRejected };
}

export type TransferItemStatus =
  | 'selected' // gewählt, noch kein Upload gestartet
  | 'queued' // wartet auf einen der 2 Slots
  | 'uploading'
  | 'waiting' // Backoff (429-Slot / transienter Retry)
  | 'done'
  | 'error';

export type TransferItem = {
  id: string;
  file: File;
  /** Name des gedroppten Top-Level-Ordners (Gruppen-Zeile in der Queue-UI,
   * WeTransfer-Parität) — null bei einzeln gewählter/gedroppter Datei. */
  folder: string | null;
  status: TransferItemStatus;
  progress: number; // 0–100
  error?: string;
  /** Anzeige-Hinweis im waiting-Status (Rate-Limit-Fenster statt Slot-Warten). */
  waitHint?: string;
};

/** addFiles-Input: nackte Files (Picker) oder ordner-getaggte Drops. */
export type AddCandidate = File | { file: File; folder: string | null };

export type TransferRejection = { name: string; reason: string };

export type FinalizeResult = {
  token: string;
  url: string;
  expiresAt: string;
  // Ist der Link später aus „Meine Transfers" re-kopierbar? (Server hat den
  // verschlüsselten Ciphertext gespeichert). Optional: ältere Server-Responses
  // ohne das Feld → als false behandeln (undefined ?? false).
  linkPersisted?: boolean;
};

export type TransferPhase = 'select' | 'uploading' | 'uploaded' | 'finalized' | 'fatal';

type OnceResult =
  | { ok: true }
  | { ok: false; status: number; error: string; retryAfterMs: number | null };

/** Backoff-Wartezeit, via wakers vorzeitig auflösbar (Abort während Backoff:
 * es gibt keinen aktiven XHR zum Abbrechen — der Waker weckt den Timer). */
function delayInterruptible(
  ms: number,
  id: string,
  wakers: Map<string, () => void>,
): Promise<void> {
  return new Promise((resolve) => {
    const done = (): void => {
      clearTimeout(timer);
      if (wakers.get(id) === done) wakers.delete(id);
      resolve();
    };
    const timer = setTimeout(done, ms);
    wakers.set(id, done);
  });
}

/** Ein einzelner POST /api/transfers/:id/files via XHR (Progress + Abort). */
function postFileOnce(
  createXhr: () => XMLHttpRequest,
  draftId: string,
  file: File,
  folder: string | null,
  onProgress: (pct: number) => void,
  activeXhrs: Map<string, XMLHttpRequest>,
  itemId: string,
): Promise<OnceResult> {
  return new Promise((resolve) => {
    const form = new FormData();
    // Felder VOR dem (potenziell riesigen) File-Part — der Server liest die
    // Felder zwar erst nach dem vollen Parse, aber so bleiben sie auch für
    // streamende Verarbeitung vorn. folder = Gruppierung der /t-Seite;
    // relpath = Pfad INNERHALB des Ordners (File.name trägt ihn seit dem
    // Traversal, busboy strippt ihn aus dem multipart-filename) — Struktur-
    // Quelle für den Ordner-ZIP-Download.
    if (folder) {
      form.append('folder', folder);
      form.append('relpath', file.name);
    }
    form.append('file', file);
    const xhr = createXhr();
    activeXhrs.set(itemId, xhr);
    const settle = (result: OnceResult): void => {
      if (activeXhrs.get(itemId) === xhr) activeXhrs.delete(itemId);
      resolve(result);
    };
    xhr.open('POST', `/api/transfers/${encodeURIComponent(draftId)}/files`);
    xhr.withCredentials = true;
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      const header = xhr.getResponseHeader('Retry-After');
      const parsed = header ? Number(header) : NaN;
      const retryAfterMs = Number.isFinite(parsed) && parsed >= 0 ? parsed * 1000 : null;
      try {
        const body = JSON.parse(xhr.responseText) as { id?: string; error?: string };
        if (xhr.status >= 200 && xhr.status < 300 && body.id) {
          settle({ ok: true });
        } else {
          settle({
            ok: false,
            status: xhr.status,
            error: body.error ?? `HTTP ${xhr.status}`,
            retryAfterMs,
          });
        }
      } catch {
        settle({ ok: false, status: xhr.status, error: `HTTP ${xhr.status}`, retryAfterMs });
      }
    };
    xhr.onerror = () => settle({ ok: false, status: 0, error: 'Netzwerkfehler', retryAfterMs: null });
    xhr.onabort = () => settle({ ok: false, status: 0, error: 'Abgebrochen', retryAfterMs: null });
    xhr.send(form);
  });
}

export type UseTransferUploadOptions = {
  /** Test-Injektion: XHR-Factory (Default: echtes XMLHttpRequest). */
  createXhr?: () => XMLHttpRequest;
};

// Modul-stabil (kein neues Arrow pro Render → useCallback-Deps bleiben ruhig).
const defaultCreateXhr = (): XMLHttpRequest => new XMLHttpRequest();

export function useTransferUpload(options?: UseTransferUploadOptions) {
  const createXhr = options?.createXhr ?? defaultCreateXhr;

  const [items, setItems] = useState<TransferItem[]>([]);
  const [phase, setPhase] = useState<TransferPhase>('select');
  const [rejections, setRejections] = useState<TransferRejection[]>([]);
  const [draftId, setDraftId] = useState<string | null>(null);
  const [draftExpiresAt, setDraftExpiresAt] = useState<string | null>(null);
  const [draftError, setDraftError] = useState<string | null>(null);
  const [flowError, setFlowError] = useState<string | null>(null);
  const [finalizeError, setFinalizeError] = useState<string | null>(null);
  const [finalizeResult, setFinalizeResult] = useState<FinalizeResult | null>(null);

  // Render-synchrone Spiegel (stale-closure-frei bei stabilen Callbacks).
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const phaseRef = useRef(phase);
  phaseRef.current = phase;

  const draftIdRef = useRef<string | null>(null);
  const abortedRef = useRef(false);
  const fatalRef = useRef(false);
  const uploadingRef = useRef(false);
  // Single-Flight für finalize(): ein Doppelklick würde zwei /finalize senden —
  // der Server gibt das Klartext-Token nur dem ERSTEN, das 409 des Duplikats
  // würde phase=fatal setzen und das Einmal-Token verstecken (Codex-PR-R2 [P1]).
  const finalizingRef = useRef(false);
  // run-token: cancel/reset invalidieren laufende Queue-Continuations —
  // sonst überschreibt ein settelndes Promise.all den frisch resetteten State.
  const runTokenRef = useRef(0);
  const activeXhrs = useRef<Map<string, XMLHttpRequest>>(new Map());
  const wakersRef = useRef<Map<string, () => void>>(new Map());
  const mountedRef = useRef(true);

  // Unmount: XHRs abbrechen, Backoff-Waiter wecken — Draft bleibt bewusst
  // stehen (24h-Worker räumt). mountedRef im Effect-SETUP setzen (StrictMode).
  useEffect(() => {
    mountedRef.current = true;
    const xhrs = activeXhrs.current;
    const wakers = wakersRef.current;
    return () => {
      mountedRef.current = false;
      abortedRef.current = true;
      for (const xhr of xhrs.values()) xhr.abort();
      xhrs.clear();
      for (const wake of [...wakers.values()]) wake();
      wakers.clear();
    };
  }, []);

  const updateItem = useCallback((id: string, patch: Partial<TransferItem>): void => {
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, ...patch } : i)));
  }, []);

  /**
   * Client-Vorvalidierung gegen die SSOT-Caps — KEIN Request für Abgelehntes.
   * Partial accept in Auswahl-Reihenfolge: >10-GiB-Dateien einzeln raus,
   * danach der Reihe nach übernehmen bis Datei-/Byte-Cap; alle Ablehnungen
   * eines Adds landen gesammelt in `rejections` (Sammel-Hinweis).
   * `preRejected` = bereits vorgelagert Abgelehntes desselben Adds (Ordner-
   * Drops aus collectDroppedFiles) — wird mit angezeigt statt überschrieben.
   */
  const addFiles = useCallback((incoming: AddCandidate[], preRejected: TransferRejection[] = []): void => {
    const p = phaseRef.current;
    if (p === 'uploading' || p === 'finalized' || p === 'fatal') return;
    const current = itemsRef.current;
    let count = current.length;
    let total = current.reduce((sum, i) => sum + i.file.size, 0);
    const accepted: TransferItem[] = [];
    const rejected: TransferRejection[] = [];
    for (const candidate of incoming) {
      const file = candidate instanceof File ? candidate : candidate.file;
      const folder = candidate instanceof File ? null : candidate.folder;
      if (file.size > TRANSFER_MAX_FILE_BYTES) {
        rejected.push({ name: file.name, reason: 'größer als 10 GB' });
        continue;
      }
      if (file.size === 0) {
        rejected.push({ name: file.name, reason: 'leere Datei' });
        continue;
      }
      if (count + 1 > TRANSFER_MAX_FILES) {
        rejected.push({ name: file.name, reason: `Limit ${TRANSFER_MAX_FILES} Dateien erreicht` });
        continue;
      }
      if (total + file.size > TRANSFER_MAX_TOTAL_BYTES) {
        rejected.push({ name: file.name, reason: 'Gesamtlimit 25 GB überschritten' });
        continue;
      }
      count += 1;
      total += file.size;
      accepted.push({
        id: crypto.randomUUID(),
        file,
        folder,
        status: 'selected',
        progress: 0,
      });
    }
    if (accepted.length > 0) setItems((prev) => [...prev, ...accepted]);
    setRejections([...preRejected, ...rejected]);
  }, []);

  /** Entfernen nur vor dem Upload (selected) oder nach Fehler (error) —
   * done-Dateien sind serverseitig committet und bleiben Teil des Drafts. */
  const removeFile = useCallback((id: string): void => {
    setItems((prev) =>
      prev.filter((i) => i.id !== id || (i.status !== 'selected' && i.status !== 'error')),
    );
  }, []);

  /** Retry-Loop für EIN Item (Achsen-Doku im Datei-Header). */
  const uploadItem = useCallback(
    async (item: TransferItem, draft: string): Promise<void> => {
      const bail = (): boolean => abortedRef.current || fatalRef.current;
      if (bail()) {
        updateItem(item.id, { status: 'error', error: 'Abgebrochen' });
        return;
      }
      updateItem(item.id, { status: 'uploading', progress: 0, error: undefined });
      let transientTries = 0;
      let rateTries = 0; // Slot-Semaphore-429s (Retry-After ≤ 30 s)
      let rateLimitWaits = 0; // User-Rate-Limit-429s (Retry-After > 30 s)
      for (;;) {
        // Lesbarkeits-Probe vor JEDEM Versuch (auch Retries): ein stale File
        // ist deterministisch tot — ohne Probe verbrennt die Ladder 3 Versuche
        // mit irreführendem „Netzwerkfehler" (siehe isFileReadable-Doku).
        if (!(await isFileReadable(item.file))) {
          updateItem(item.id, { status: 'error', error: FILE_CHANGED_MESSAGE });
          return;
        }
        if (bail()) {
          updateItem(item.id, { status: 'error', error: 'Abgebrochen' });
          return;
        }
        const res = await postFileOnce(
          createXhr,
          draft,
          item.file,
          item.folder,
          (pct) => updateItem(item.id, { progress: pct }),
          activeXhrs.current,
          item.id,
        );
        if (bail()) {
          updateItem(item.id, { status: 'error', error: 'Abgebrochen' });
          return;
        }
        if (res.ok) {
          updateItem(item.id, { status: 'done', progress: 100 });
          return;
        }
        if (res.status === 409) {
          // Terminal: Draft existiert nicht mehr / ist nicht mehr draft.
          // Queue stoppen — Retry wäre deterministisch erfolglos.
          fatalRef.current = true;
          setFlowError(FLOW_ERROR_MESSAGE);
          updateItem(item.id, { status: 'error', error: 'Transfer nicht mehr verfügbar' });
          for (const xhr of activeXhrs.current.values()) xhr.abort();
          for (const wake of [...wakersRef.current.values()]) wake();
          return;
        }
        if (res.status === 429) {
          const retryMs = res.retryAfterMs ?? DEFAULT_RETRY_AFTER_MS;
          if (retryMs > RATE_LIMIT_THRESHOLD_MS) {
            // User-Rate-Limit (10 Uploads/15 min): echtes Fenster abwarten —
            // verbraucht NICHT den Slot-Cap, sonst failen die Dateien 11–20
            // eines 20er-Batches deterministisch (Codex-PR-R1 [P2]).
            rateLimitWaits += 1;
            if (rateLimitWaits > RATE_LIMIT_MAX_WAITS) {
              updateItem(item.id, {
                status: 'error',
                error: 'Upload-Limit erreicht — bitte später erneut versuchen',
              });
              return;
            }
            const waitMs = Math.min(retryMs, RATE_LIMIT_MAX_WAIT_MS);
            updateItem(item.id, {
              status: 'waiting',
              progress: 0,
              waitHint: `Upload-Limit erreicht — wartet ~${Math.max(1, Math.round(waitMs / 60_000))} min`,
            });
            await delayInterruptible(waitMs, item.id, wakersRef.current);
            if (bail()) {
              updateItem(item.id, { status: 'error', error: 'Abgebrochen' });
              return;
            }
            updateItem(item.id, { status: 'uploading', progress: 0, waitHint: undefined });
            continue;
          }
          // Slot-Semaphore (Gate MAX 2 voll): kurzer Backoff, Cap 5.
          rateTries += 1;
          if (rateTries >= RATE_MAX_ATTEMPTS) {
            updateItem(item.id, {
              status: 'error',
              error: 'Server ausgelastet — bitte später erneut versuchen',
            });
            return;
          }
          updateItem(item.id, { status: 'waiting', progress: 0, waitHint: undefined });
          const waitMs = Math.min(Math.max(retryMs, RETRY_AFTER_MIN_MS), RETRY_AFTER_MAX_MS);
          await delayInterruptible(waitMs, item.id, wakersRef.current);
          if (bail()) {
            updateItem(item.id, { status: 'error', error: 'Abgebrochen' });
            return;
          }
          updateItem(item.id, { status: 'uploading', progress: 0 });
          continue;
        }
        if (RETRYABLE_TRANSIENT_STATUS.has(res.status)) {
          transientTries += 1;
          if (transientTries >= TRANSIENT_MAX_ATTEMPTS) {
            updateItem(item.id, { status: 'error', error: res.error });
            return;
          }
          updateItem(item.id, { status: 'waiting', progress: 0 });
          await delayInterruptible(
            TRANSIENT_RETRY_BASE_MS * transientTries,
            item.id,
            wakersRef.current,
          );
          if (bail()) {
            updateItem(item.id, { status: 'error', error: 'Abgebrochen' });
            return;
          }
          updateItem(item.id, { status: 'uploading', progress: 0 });
          continue;
        }
        // 413/400/403/404/500 … → sofortiger Fehler, manueller Retry bleibt.
        updateItem(item.id, { status: 'error', error: res.error });
        return;
      }
    },
    [createXhr, updateItem],
  );

  /**
   * „Hochladen": legt den Draft an (falls noch keiner existiert — Nachlegen
   * nutzt denselben Draft) und schiebt alle `selected`-Items durch die
   * Concurrency-2-Queue. Draft-Create-Fehler → `draftError` (Inline-Anzeige),
   * kein Upload-Start ohne id.
   */
  const upload = useCallback(
    async (opts: {
      expiryDays: TransferExpiryDays;
      message: string;
      /** Optionales Download-Passwort (WeTransfer-Stil); leer = kein Schutz. */
      password?: string;
    }): Promise<void> => {
      if (uploadingRef.current || fatalRef.current) return;
      const p = phaseRef.current;
      if (p !== 'select' && p !== 'uploaded') return;
      const selected = itemsRef.current.filter((i) => i.status === 'selected');
      if (selected.length === 0) return;

      // Guard VOR dem Draft-Create setzen: ein Doppelklick während des langsamen
      // POST würde sonst ZWEI Drafts anlegen und dieselben Dateien in beide
      // hochladen (Codex-PR-R2 [P2]). Bei Draft-Fehler wieder freigeben.
      uploadingRef.current = true;
      setDraftError(null);
      let draft = draftIdRef.current;
      if (!draft) {
        try {
          const res = await api<{ id: string; expiresAt: string }>('/api/transfers', {
            method: 'POST',
            body: JSON.stringify({
              expiryDays: opts.expiryDays,
              message: opts.message.trim() || null,
              // undefined → JSON.stringify lässt den Key weg → Server: kein Passwort.
              password: opts.password?.trim() || undefined,
            }),
          });
          draft = res.id;
          draftIdRef.current = draft;
          if (!mountedRef.current) return;
          setDraftId(draft);
          setDraftExpiresAt(res.expiresAt);
        } catch (err) {
          uploadingRef.current = false;
          if (!mountedRef.current) return;
          setDraftError(
            err instanceof Error ? err.message : 'Transfer konnte nicht angelegt werden',
          );
          return;
        }
      }

      abortedRef.current = false;
      const myToken = runTokenRef.current;
      setPhase('uploading');
      for (const i of selected) updateItem(i.id, { status: 'queued' });
      const limit = pLimit(CONCURRENCY);
      await Promise.all(selected.map((i) => limit(() => uploadItem(i, draft as string))));
      uploadingRef.current = false;
      // Stale Continuation (Cancel/Reset lief dazwischen) → kein Phase-Write.
      if (!mountedRef.current || myToken !== runTokenRef.current) return;
      setPhase(fatalRef.current ? 'fatal' : 'uploaded');
    },
    [updateItem, uploadItem],
  );

  /** Manueller Retry einer Fehler-Zeile — resettet beide Retry-Zähler. */
  const retryFile = useCallback(
    async (id: string): Promise<void> => {
      const item = itemsRef.current.find((i) => i.id === id);
      const draft = draftIdRef.current;
      if (!item || item.status !== 'error' || !draft) return;
      if (uploadingRef.current || fatalRef.current) return;
      abortedRef.current = false;
      uploadingRef.current = true;
      const myToken = runTokenRef.current;
      setPhase('uploading');
      await uploadItem(item, draft);
      uploadingRef.current = false;
      if (!mountedRef.current || myToken !== runTokenRef.current) return;
      setPhase(fatalRef.current ? 'fatal' : 'uploaded');
    },
    [uploadItem],
  );

  /** „Link erstellen" — Token kommt GENAU EINMAL im Klartext zurück.
   * Single-Flight (finalizingRef) + finalized-Re-Check: ein Duplikat-409 darf
   * das bereits angezeigte Einmal-Token NIE verstecken (Codex-PR-R2 [P1]). */
  const finalize = useCallback(async (): Promise<void> => {
    const draft = draftIdRef.current;
    if (!draft || uploadingRef.current || fatalRef.current || finalizingRef.current) return;
    if (phaseRef.current === 'finalized') return;
    finalizingRef.current = true;
    // run-token capturen: klickt der User während des in-flight /finalize auf
    // „Abbrechen" (cancelAndDelete → Reset + DELETE), darf die späte Antwort
    // weder ein Link-Panel für den gelöschten Transfer zeigen noch fatal/
    // finalizeError auf die frische UI schreiben (Codex-PR-R3 [P2]).
    const myToken = runTokenRef.current;
    setFinalizeError(null);
    try {
      const res = await api<FinalizeResult>(
        `/api/transfers/${encodeURIComponent(draft)}/finalize`,
        { method: 'POST' },
      );
      if (!mountedRef.current || myToken !== runTokenRef.current) return;
      setFinalizeResult(res);
      setPhase('finalized');
    } catch (err) {
      if (!mountedRef.current || myToken !== runTokenRef.current) return;
      // Defense-in-depth: sollte je eine stale Antwort NACH erfolgreichem
      // Finalize settlen, darf sie den Token-Zustand nicht überschreiben.
      // (Cast: TS narrowt den Ref-Read über awaits sonst fälschlich weg —
      // der Ref-Spiegel ändert sich aber mit jedem Re-Render.)
      if ((phaseRef.current as TransferPhase) === 'finalized') return;
      if (err instanceof ApiError && err.status === 409) {
        fatalRef.current = true;
        setFlowError(FLOW_ERROR_MESSAGE);
        setPhase('fatal');
        return;
      }
      setFinalizeError(err instanceof Error ? err.message : 'Link konnte nicht erstellt werden');
    } finally {
      finalizingRef.current = false;
    }
  }, []);

  /**
   * „Hochladen & Link erstellen" (Ein-Button-Flow): lädt hoch und finalisiert
   * danach automatisch, wenn der Upload sauber durchlief → der Link erscheint
   * ohne zweiten Klick. `upload` selbst bleibt bewusst rein (kein Finalize) —
   * Nachlegen/removeFile vor dem Senden funktionieren weiter.
   *
   * Die Auto-finalize-ENTSCHEIDUNG trifft der Effect unten (nicht hier direkt
   * nach `await upload`): `itemsRef` wird nur beim Render gesynct → ein Check
   * direkt nach `await upload` läse es evtl. stale. Der Effect läuft NACH dem
   * Render → `items` ist aktuell. Das Flag markiert nur „dieser Upload wurde
   * über den Ein-Button-Flow gestartet".
   */
  const autoFinalizeRef = useRef(false);

  const uploadAndFinalize = useCallback(
    async (opts: {
      expiryDays: TransferExpiryDays;
      message: string;
      password?: string;
    }): Promise<void> => {
      autoFinalizeRef.current = true;
      await upload(opts);
    },
    [upload],
  );

  useEffect(() => {
    if (!autoFinalizeRef.current) return;
    // Upload läuft noch → warten (Progress-Ticks feuern den Effect, no-op).
    if (phase === 'select' || phase === 'uploading') return;
    // Terminaler Upload-Ausgang erreicht → Entscheidung treffen + Flag zurücksetzen.
    autoFinalizeRef.current = false;
    // Nur bei vollständig sauberem Upload (ALLE Items `done`) finalisieren. Bei
    // Teil-Fehlern / fatalem Ausgang bleibt der manuelle „Link erstellen"-Fallback
    // (kein stilles Auslassen einer Datei, Codex-PR-R1 [P2]).
    if (phase === 'uploaded' && items.length > 0 && items.every((i) => i.status === 'done')) {
      void finalize();
    }
  }, [phase, items, finalize]);

  const resetState = useCallback((): void => {
    draftIdRef.current = null;
    abortedRef.current = false;
    fatalRef.current = false;
    uploadingRef.current = false;
    // Auto-finalize-Flag mit-zurücksetzen: sonst überlebt ein Cancel/Reset mitten
    // im 'uploading' (geht direkt auf 'select', der Effect-Reset greift nicht) und
    // ein späterer reiner `upload()` würde fälschlich auto-finalisieren (Contract:
    // `upload` bleibt rein). secfix-review Sonnet regression-lens.
    autoFinalizeRef.current = false;
    setItems([]);
    setPhase('select');
    setRejections([]);
    setDraftId(null);
    setDraftExpiresAt(null);
    setDraftError(null);
    setFlowError(null);
    setFinalizeError(null);
    setFinalizeResult(null);
  }, []);

  /**
   * „Abbrechen" / Nav-Guard-OK: laufende XHRs aborten, Backoff-Waiter wecken,
   * Draft best-effort löschen (DELETE räumt Row + S3 sofort; Fehler egal —
   * der 24h-Worker ist der Backstop), dann frischer Zustand.
   */
  const cancelAndDelete = useCallback(async (): Promise<void> => {
    abortedRef.current = true;
    runTokenRef.current += 1;
    for (const xhr of activeXhrs.current.values()) xhr.abort();
    activeXhrs.current.clear();
    for (const wake of [...wakersRef.current.values()]) wake();
    wakersRef.current.clear();
    const draft = draftIdRef.current;
    if (draft) {
      await api(`/api/transfers/${encodeURIComponent(draft)}`, { method: 'DELETE' }).catch(
        () => {
          /* best-effort */
        },
      );
    }
    if (!mountedRef.current) return;
    resetState();
  }, [resetState]);

  /** „Neuer Transfer" nach Finalize/Fatal — Server-Objekt bleibt unangetastet. */
  const reset = useCallback((): void => {
    runTokenRef.current += 1;
    resetState();
  }, [resetState]);

  const someDone = items.some((i) => i.status === 'done');
  // dirty steuert den zweistufigen Verlassen-Schutz der Page: Upload läuft
  // ODER es existiert ein unfinalisierter Draft mit committeten Dateien.
  const dirty =
    phase === 'uploading' || (draftId !== null && phase === 'uploaded' && someDone);

  return {
    items,
    phase,
    rejections,
    draftId,
    draftExpiresAt,
    draftError,
    flowError,
    finalizeError,
    finalizeResult,
    dirty,
    addFiles,
    removeFile,
    upload,
    uploadAndFinalize,
    retryFile,
    finalize,
    cancelAndDelete,
    reset,
  };
}
